<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'

/**
 * A mail's HTML as its sender laid it out (#1112): its own styles, tables and images, in a frame of its own so none
 * of it reaches the portal. The frame's sandbox grants no scripts, so nothing in a mail runs whatever the server's
 * allowlist missed; `allow-same-origin` only lets this page read the frame's height, which a scriptless frame cannot
 * use. Links leave in a new tab outside the sandbox. The frame keeps a light page in both portal themes, since a
 * mail is designed for one, and grows to its content so the mailbox scrolls, not the mail.
 */
const props = defineProps<{ html: string }>()

/** No script, no form, no frame, whatever the HTML says: the allowlist's second wall. */
const POLICY = [
    "default-src 'none'",
    'img-src https: http: data:',
    "style-src 'unsafe-inline' https: http:",
    'font-src https: http: data:',
    "script-src 'none'",
    "form-action 'none'",
    "frame-src 'none'",
].join('; ')

const BASE_STYLE = [
    'html{color-scheme:light}',
    'body{margin:0;padding:16px;background:#fff;color:#111;font:14px/1.5 system-ui,-apple-system,sans-serif;overflow-wrap:anywhere}',
    'img{max-width:100%;height:auto}',
    'table{max-width:100%}',
].join('')

const srcdoc = computed(
    () =>
        `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${POLICY}">` +
        `<base target="_blank"><style>${BASE_STYLE}</style></head><body>${props.html}</body></html>`,
)

const frame = ref<HTMLIFrameElement | null>(null)
const height = ref(120)
let observer: ResizeObserver | null = null

/** The frame's height follows its content: on load, as each image arrives, and when the frame's width changes. */
function measure(): void {
    const doc = frame.value?.contentDocument
    if (!doc?.documentElement) {
        return
    }
    height.value = Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight ?? 0, 40)
}

function loaded(): void {
    measure()
    const doc = frame.value?.contentDocument
    for (const image of Array.from(doc?.images ?? [])) {
        if (!image.complete) {
            image.addEventListener('load', measure, { once: true })
            image.addEventListener('error', measure, { once: true })
        }
    }
    observer?.disconnect()
    if (typeof ResizeObserver !== 'undefined' && frame.value) {
        observer = new ResizeObserver(measure)
        observer.observe(frame.value)
    }
}

onBeforeUnmount(() => observer?.disconnect())
</script>

<template>
	<iframe
		ref="frame"
		:srcdoc="srcdoc"
		sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
		referrerpolicy="no-referrer"
		title="Mail content"
		class="border-border block w-full rounded border bg-white"
		:style="{ height: `${height}px` }"
		data-testid="mail-html"
		@load="loaded"
	/>
</template>
